import {styled} from '@linaria/react';
import React, {FC, useMemo} from 'react';
import {COLORS} from '../../../constants/colors';
import {FONT_SIZES, FONT_WEIGHTS} from '../../../constants/typography';
import {UnstyledButton} from '../../atoms/Buttons/UnstyledButton';
import {ContentPopperProps} from '../../atoms/ContentPopper/ContentPopper';
import {tabFocusedStyles} from '../../atoms/TabFocused/tabFocusedStyles';
import {BannerCountdown, BannerCountdownData} from './BannerCountdown';
import {BannerInfoIcon, BannerInfoIconData} from './BannerInfoIcon';
import {BannerMedia, BannerMediaData} from './BannerMedia';
import {BannerProductImage, BannerProductImages} from './BannerProductImages';
import {
  PROMOTION_BANNER_CONTAINER,
  bannerWideAndUpLinaria,
} from './bannerContainer';
import {HIGHLIGHT_GLOWS, SPLIT_PANEL_GLOWS, buildGlowsCss} from './glows';

/**
 * Header Highlight banners place the headline, an optional subline and a
 * countdown inside a fixed-height box (113px mobile / 180px desktop), so a long
 * headline quickly looks cramped. Mirroring the native app (mobile MR 3104,
 * which shrinks the headline + subline block uniformly with a FittedBox), we
 * derive a single scale factor from the headline's character length and let the
 * headline and subline shrink together by it. The app measures the rendered
 * text, but the web app renders server-side where measurement isn't available,
 * so we approximate the shrink from content length — the same render-time,
 * SSR-safe approach already used by `DiscountCard`.
 *
 * Returns 1 (full size) for short headlines and only shrinks, gently and with a
 * coarse floor, as the headline grows. Per-element minimum font sizes are
 * additionally enforced in CSS via `max()`.
 *
 * @param headline The banner headline; its length drives the shared factor.
 * @returns A scale factor in the range [MIN_SCALE, 1].
 */
export const getBannerTextScale = (headline: string): number => {
  // Coarse lower bound on the shared factor; per-element px floors in CSS keep
  // the headline and subline individually legible.
  const MIN_SCALE = 0.7;
  // Headline length that still fits the two-line box at full size. Tuned to the
  // tighter mobile box; the wider desktop box rarely needs to shrink.
  const SHRINK_START = 24;
  // Fraction of the base size shed per character beyond SHRINK_START.
  const SHRINK_PER_CHAR = 0.012;

  const overflowingCharacters = headline.length - SHRINK_START;
  if (overflowingCharacters <= 0) {
    return 1;
  }

  return Math.max(MIN_SCALE, 1 - overflowingCharacters * SHRINK_PER_CHAR);
};

export interface BannerCta {
  label: string;
  colors: {
    foregroundColor: {hex: string};
    backgroundColor: {hex: string};
  };
}

export interface HighlightBannerData {
  headline: string;
  subline?: string;
  cta?: BannerCta;
  media?: BannerMediaData;
  /** Hex color used for headline / subline / icon tint. */
  foregroundColor: string;
  backgroundColor?: string;
  /** Hex stops for the radial-gradient glows. */
  gradientColors?: string[];
  infoIcon?: BannerInfoIconData;
  countdown?: BannerCountdownData;
  productImages?: BannerProductImage[];
}

interface HighlightBannerProps {
  banner: HighlightBannerData;
  onClick?: VoidFunction;
  ContentPopperLayoutComponent: ContentPopperProps['LayoutComponent'];
  className?: string;
  ['data-testid']?: string;
}

export const HighlightBanner: FC<HighlightBannerProps> = ({
  banner,
  onClick,
  ContentPopperLayoutComponent,
  className,
  'data-testid': dataTestid,
}) => {
  const {
    headline,
    subline,
    cta,
    media,
    foregroundColor,
    backgroundColor,
    gradientColors,
    infoIcon,
    countdown,
    productImages,
  } = banner;

  const hasSplitMedia = Boolean(media?.src) && Boolean(media?.backgroundColor);
  // Simple = truly minimal (centered) layout. Anything that introduces a
  // bottom row — CTA, products, or a split-panel hero — drops us into the
  // complex layout so the start-slot countdown rule has somewhere to land.
  const isSimple =
    !cta && (!productImages || productImages.length === 0) && !hasSplitMedia;
  const isSplitPanel = !isSimple && hasSplitMedia;
  const ariaLabel = subline ? `${headline} — ${subline}` : headline;

  const fallbackBg = media?.backgroundColor ?? COLORS['Fill.Grey.Inactive'];
  const glowCss = buildGlowsCss(
    isSplitPanel ? SPLIT_PANEL_GLOWS : HIGHLIGHT_GLOWS,
    gradientColors,
  );

  return (
    <Wrapper
      className={className}
      data-testid={dataTestid}
      data-has-info-icon={infoIcon ? '' : undefined}
    >
      <BannerButton
        type="button"
        aria-label={ariaLabel}
        onClick={onClick}
        data-variant="highlightBanner"
      >
        <BgLayer style={{backgroundColor: fallbackBg}} />
        {gradientColors && gradientColors.length > 0 && (
          <GlowLayer style={{backgroundImage: glowCss}} />
        )}
        {!isSplitPanel && media?.src && (
          <MediaLayer>
            <BannerMedia media={media} />
          </MediaLayer>
        )}

        {isSimple ? (
          <SimpleContent
            headline={headline}
            subline={subline}
            foregroundColor={foregroundColor}
            countdown={countdown}
          />
        ) : (
          <ComplexContent
            headline={headline}
            cta={cta}
            countdown={countdown}
            productImages={productImages}
            foregroundColor={foregroundColor}
            isSplitPanel={isSplitPanel}
            media={media}
            glowCss={glowCss}
            backgroundColor={media?.backgroundColor}
          />
        )}
      </BannerButton>

      {infoIcon && (
        <BannerInfoIcon
          content={infoIcon.content}
          foregroundColor={foregroundColor}
          ContentPopperLayoutComponent={ContentPopperLayoutComponent}
          countdown={
            infoIcon.countdownEndsAt
              ? {
                  endsAt: infoIcon.countdownEndsAt,
                  backgroundColor,
                  foregroundColor,
                }
              : undefined
          }
        />
      )}
    </Wrapper>
  );
};

interface SimpleContentProps {
  headline: string;
  subline?: string;
  foregroundColor: string;
  countdown?: BannerCountdownData;
}

const SimpleContent: FC<SimpleContentProps> = ({
  headline,
  subline,
  foregroundColor,
  countdown,
}) => {
  // Headline + subline shrink together by one character-length factor so long
  // copy stays inside the banner's fixed height instead of truncating (see
  // getBannerTextScale). The factor rides down to the CSS as a custom property.
  const textScale = useMemo(() => getBannerTextScale(headline), [headline]);

  return (
    <SimpleColumn
      style={
        {
          color: foregroundColor,
          '--banner-text-scale': textScale,
        } as React.CSSProperties
      }
    >
      <SimpleHeadline>{headline}</SimpleHeadline>
      {subline && <SimpleSubline>{subline}</SimpleSubline>}
      {countdown && (
        <CountdownGap>
          <BannerCountdown
            endsAt={countdown.endsAt}
            foregroundColor={foregroundColor}
          />
        </CountdownGap>
      )}
    </SimpleColumn>
  );
};

interface ComplexContentProps {
  headline: string;
  cta?: BannerCta;
  countdown?: BannerCountdownData;
  productImages?: BannerProductImage[];
  foregroundColor: string;
  isSplitPanel: boolean;
  media?: BannerMediaData;
  glowCss: string;
  backgroundColor?: string;
}

const ComplexContent: FC<ComplexContentProps> = ({
  headline,
  cta,
  countdown,
  productImages,
  foregroundColor,
  isSplitPanel,
  media,
  glowCss,
  backgroundColor,
}) => {
  const visibleProducts =
    productImages && productImages.length > 0 ? productImages : undefined;
  // Special rule: when CTA is absent (and no product strip taking the slot),
  // render the countdown in the bottom-start slot instead of bottom-end.
  const countdownInStartSlot = !cta && !visibleProducts && Boolean(countdown);

  let bottomSlot: React.ReactNode = null;
  if (visibleProducts) {
    bottomSlot = (
      <BannerProductImages products={visibleProducts} size="highlight" />
    );
  } else if (cta) {
    bottomSlot = (
      <CtaButton
        style={{
          backgroundColor: cta.colors.backgroundColor.hex,
          color: cta.colors.foregroundColor.hex,
        }}
      >
        {cta.label}
      </CtaButton>
    );
  } else if (countdownInStartSlot && countdown) {
    bottomSlot = (
      <BannerCountdown
        endsAt={countdown.endsAt}
        foregroundColor={foregroundColor}
        data-position="start"
      />
    );
  }

  const innerContent = (
    <ContentColumn style={{color: foregroundColor}}>
      <ComplexHeadline>{headline}</ComplexHeadline>
      <BottomRow>{bottomSlot}</BottomRow>
    </ContentColumn>
  );

  if (isSplitPanel) {
    return (
      <>
        <HeroSlot>
          <BannerMedia media={media} />
        </HeroSlot>
        <LeftPanel
          style={{
            backgroundColor: backgroundColor ?? COLORS['Fill.Grey.Inactive'],
          }}
        >
          <GlowLayer style={{backgroundImage: glowCss}} />
          {innerContent}
        </LeftPanel>
        {countdown && !countdownInStartSlot && (
          <CountdownEndSlot data-split-panel="">
            <BannerCountdown
              endsAt={countdown.endsAt}
              foregroundColor={foregroundColor}
              data-position="end"
            />
          </CountdownEndSlot>
        )}
      </>
    );
  }

  return (
    <>
      {innerContent}
      {countdown && !countdownInStartSlot && (
        <CountdownEndSlot>
          <BannerCountdown
            endsAt={countdown.endsAt}
            foregroundColor={foregroundColor}
            data-position="end"
          />
        </CountdownEndSlot>
      )}
    </>
  );
};

const Wrapper = styled.div`
  position: relative;
  width: 100%;
  /* Establish the query container so the banner adapts to its own width
     (full-width vs. narrow column) rather than the viewport. */
  container-type: inline-size;
  container-name: ${PROMOTION_BANNER_CONTAINER};
`;

const BannerButton = styled(UnstyledButton)`
  position: relative;
  display: block;
  width: 100%;
  height: 113px;
  border-radius: 4px;
  overflow: hidden;
  text-align: start;
  /* Mask trick (same as DiscountCard) for Safari overflow + transform. */
  -webkit-mask-image: -webkit-radial-gradient(white, black);

  &:focus-visible {
    ${tabFocusedStyles({offset: '-4px'})}
  }

  ${bannerWideAndUpLinaria(`
    height: 180px;
  `)}
`;

const BgLayer = styled.span`
  position: absolute;
  inset: 0;
  display: block;
`;

const GlowLayer = styled.span`
  position: absolute;
  inset: 0;
  display: block;
  pointer-events: none;
`;

const MediaLayer = styled.span`
  position: absolute;
  inset: 0;
  display: block;
`;

const SimpleColumn = styled.span`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 12px 12px 16px;
  gap: 4px;
  pointer-events: none;

  /* Reserve right-side space when an info icon sits over the banner. */
  [data-has-info-icon] & {
    padding-inline-end: 32px;
  }

  ${bannerWideAndUpLinaria(`
    padding: 24px 40px;
    gap: 8px;
  `)}
`;

const SimpleHeadline = styled.span`
  /* Shrinks with --banner-text-scale (set per banner from the headline length),
     never below a legible floor, and falls back to full size when unset. */
  font-size: max(
    ${FONT_SIZES.px14},
    calc(${FONT_SIZES.px20} * var(--banner-text-scale, 1))
  );
  font-weight: ${FONT_WEIGHTS.bold};
  line-height: 18px;
  color: inherit;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;

  ${bannerWideAndUpLinaria(`
    font-size: max(${FONT_SIZES.px24}, calc(${FONT_SIZES.px40} * var(--banner-text-scale, 1)));
    line-height: 44px;
    font-weight: ${FONT_WEIGHTS.heavy};
  `)}
`;

const SimpleSubline = styled.span`
  /* Scales by the same --banner-text-scale factor as the headline, with its own
     legibility floor; the ellipsis stays the final fallback for long sublines. */
  font-size: max(
    ${FONT_SIZES.px10},
    calc(${FONT_SIZES.px12} * var(--banner-text-scale, 1))
  );
  font-weight: ${FONT_WEIGHTS.mediumBold};
  line-height: 14px;
  color: inherit;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;

  ${bannerWideAndUpLinaria(`
    font-size: max(${FONT_SIZES.px12}, calc(${FONT_SIZES.px16} * var(--banner-text-scale, 1)));
    line-height: 24px;
  `)}
`;

const CountdownGap = styled.span`
  display: inline-flex;
  margin-top: 4px;
`;

const ContentColumn = styled.span`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-start;
  padding: 8px 12px 12px;
  pointer-events: none;

  /* Reserve right-side space when an info icon sits over the banner. */
  [data-has-info-icon] & {
    padding-inline-end: 32px;
  }

  ${bannerWideAndUpLinaria(`
    padding: 12px 24px 24px;
  `)}
`;

const ComplexHeadline = styled.span`
  font-weight: ${FONT_WEIGHTS.bold};
  color: inherit;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  width: 100%;
  /* Default = full-panel size. The split-panel size is applied via a
     descendant selector defined on LeftPanel below. */
  font-size: ${FONT_SIZES.px20};
  line-height: 24px;

  ${bannerWideAndUpLinaria(`
    font-size: ${FONT_SIZES.px32};
    line-height: 40px;
    font-weight: ${FONT_WEIGHTS.heavy};
  `)}
`;

const BottomRow = styled.span`
  display: inline-flex;
  align-items: center;
`;

const CtaButton = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 32px;
  padding: 0 16px;
  border-radius: 4px;
  font-size: ${FONT_SIZES.px14};
  font-weight: ${FONT_WEIGHTS.bold};
  line-height: 1;
  white-space: nowrap;

  ${bannerWideAndUpLinaria(`
    height: 48px;
    padding: 0 24px;
  `)}
`;

const HeroSlot = styled.span`
  position: absolute;
  inset-block: 0;
  inset-inline-end: 0;
  /* (343 - 184) / 343 ≈ 46.36% */
  width: 46.36%;
  display: block;
  overflow: hidden;

  ${bannerWideAndUpLinaria(`
    /* Desktop: ~437 / 835 ≈ 52.34% so the media slightly underlaps the
       LeftPanel's 16px rounded corner. */
    width: 52.34%;
  `)}
`;

const LeftPanel = styled.span`
  position: absolute;
  inset-block: 0;
  inset-inline-start: 0;
  /* 196 / 343 ≈ 57.14% (slightly overlaps the hero) */
  width: 57.14%;
  display: block;
  border-start-end-radius: 16px;
  border-end-end-radius: 16px;
  overflow: hidden;

  /* In the narrow split-panel column, the headline drops to a smaller size. */
  ${ComplexHeadline} {
    font-size: ${FONT_SIZES.px16};
    line-height: 20px;
  }

  /*
   * The info icon overlaps the hero — not the left panel — in this layout,
   * so undo the right-side padding the wrapper adds for the full layout.
   */
  ${ContentColumn} {
    padding-inline-end: 12px;
  }

  ${bannerWideAndUpLinaria(`
    /* Wide: 418 / 835 ≈ 50% true split with the media. */
    width: 50.06%;

    /* In the wide layout the split-panel headline matches the full-panel size. */
    ${ComplexHeadline} {
      font-size: ${FONT_SIZES.px32};
      line-height: 40px;
      font-weight: ${FONT_WEIGHTS.heavy};
    }

    ${ContentColumn} {
      padding-inline-end: 24px;
    }
  `)}
`;

const CountdownEndSlot = styled.span`
  position: absolute;
  inset-inline-end: 12px;
  bottom: 12px;
  pointer-events: none;

  ${bannerWideAndUpLinaria(`
    inset-inline-end: 24px;
    bottom: 24px;

    /* In the split-panel layout the countdown sits inside the LEFT panel
       (not over the media), so push it ~24px in from the LeftPanel edge. */
    &[data-split-panel] {
      inset-inline-end: calc(49.94% + 24px);
    }
  `)}
`;
